import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

export default function HausmeisterPage() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', address: '', package: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) { setSubmitStatus('error'); return; }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => formBody.append(k, v));
      const response = await fetch('https://readdy.ai/api/form/d6noeefk3rg2i5v3een0', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formBody.toString() });
      if (response.ok) { setSubmitStatus('success'); setFormData({ name: '', email: '', phone: '', address: '', package: '', message: '' }); }
      else setSubmitStatus('error');
    } catch { setSubmitStatus('error'); }
    finally { setIsSubmitting(false); }
  };

  const services = [
    { icon: 'ri-tools-fill', title: 'Kleinreparaturen', description: 'Schnelle Behebung kleinerer Schäden und Defekte' },
    { icon: 'ri-plant-fill', title: 'Grünpflege', description: 'Rasenmähen, Heckenschnitt und Gartenpflege' },
    { icon: 'ri-snowy-fill', title: 'Winterdienst', description: 'Schneeräumung und Streudienst bei Eis und Schnee' },
    { icon: 'ri-delete-bin-fill', title: 'Müllentsorgung', description: 'Verwaltung und Entsorgung von Abfällen' },
    { icon: 'ri-file-list-3-fill', title: 'Hausordnung', description: 'Überwachung der Einhaltung der Hausordnung' },
    { icon: 'ri-lightbulb-flash-fill', title: 'Beleuchtung', description: 'Wartung und Austausch von Leuchtmitteln' },
    { icon: 'ri-paint-brush-fill', title: 'Kleinere Malerarbeiten', description: 'Ausbesserungen und Renovierungsarbeiten' },
    { icon: 'ri-key-fill', title: 'Schlüsselverwaltung', description: 'Sichere Verwaltung von Schlüsseln und Zugängen' },
    { icon: 'ri-shield-check-fill', title: 'Objektkontrolle', description: 'Regelmäßige Kontrolle und Dokumentation' }
  ];

  const faqs = [
    {
      question: 'Welche Vertragsmodelle bieten Sie für den Hausmeisterservice an?',
      answer: 'Wir bieten drei flexible Modelle: Einmalige Einsätze für spezielle Aufgaben, regelmäßige Betreuung nach festem Plan (wöchentlich, monatlich) und unser Rundum-Sorglos-Paket mit vollumfänglicher Immobilienbetreuung. Alle Modelle sind ohne lange Vertragsbindung buchbar und individuell auf Ihre Bedürfnisse anpassbar.'
    },
    {
      question: 'Was ist im Hausmeisterservice alles enthalten?',
      answer: 'Unser Service umfasst Kleinreparaturen, Grünpflege (Rasenmähen, Heckenschnitt), Winterdienst, Müllentsorgung, Überwachung der Hausordnung, Beleuchtungswartung, kleinere Malerarbeiten, Schlüsselverwaltung und regelmäßige Objektkontrollen. Je nach gewähltem Paket können Sie die Leistungen individuell zusammenstellen oder das Rundum-Sorglos-Paket wählen.'
    },
    {
      question: 'Wie schnell reagieren Sie bei dringenden Reparaturen?',
      answer: 'Bei dringenden Reparaturen und Notfällen sind wir in der Regel innerhalb von 2-4 Stunden vor Ort in Ratingen und Umgebung. Für Standardreparaturen vereinbaren wir einen Termin innerhalb von 24-48 Stunden. Unsere Erreichbarkeit ist Montag bis Freitag von 10:00 bis 15:00 Uhr und Samstag von 12:00 bis 15:00 Uhr garantiert.'
    },
    {
      question: 'Bieten Sie auch Winterdienst an und wie funktioniert das?',
      answer: 'Ja, unser Winterdienst sorgt zuverlässig für geräumte und gestreute Wege, Einfahrten und Parkplätze. Wir bieten Saisonverträge (November bis März) sowie Einzeleinsätze an. Bei Schneefall sind wir frühmorgens vor Ort, um die Verkehrssicherungspflicht zu erfüllen. Die Einsätze werden nach tatsächlichem Aufwand oder als Pauschale abgerechnet.'
    },
    {
      question: 'Wie werden die Kosten für den Hausmeisterservice berechnet?',
      answer: 'Die Abrechnung erfolgt transparent nach Aufwand (Stundensatz plus Material) oder als monatliche Pauschale bei regelmäßiger Betreuung. Wir erstellen Ihnen nach einem kostenlosen Vor-Ort-Termin ein individuelles, unverbindliches Angebot. Alle Leistungen werden detailliert dokumentiert und abgerechnet – keine versteckten Kosten.'
    },
    {
      question: 'Betreuen Sie nur Gewerbeimmobilien oder auch Privathaushalte?',
      answer: 'Wir betreuen sowohl Privatkunden (Einfamilienhäuser, Mehrfamilienhäuser) als auch Gewerbekunden, Hausverwaltungen und Wohnungsbaugesellschaften. Unser Service ist flexibel skalierbar – vom kleinen Einfamilienhaus bis zum großen Bürokomplex. Jeder Kunde erhält die gleiche professionelle Betreuung.'
    },
    {
      question: 'In welchen Gebieten ist Ihr Hausmeisterservice verfügbar?',
      answer: 'Wir sind hauptsächlich in Ratingen und der gesamten Region Düsseldorf/NRW tätig – einschließlich Mettmann, Erkrath, Hilden, Langenfeld, Velbert, Heiligenhaus und weiterer umliegender Städte. Sprechen Sie uns gerne an, wenn Sie unsicher sind, ob wir Ihren Standort abdecken. Für größere Objekte außerhalb unseres Standardgebiets finden wir individuelle Lösungen.'
    },
    {
      question: 'Sind Ihre Hausmeister versichert und zuverlässig geprüft?',
      answer: 'Ja, alle unsere Mitarbeiter sind fest angestellt, vollständig versichert (Haftpflicht- und Unfallversicherung) und sorgfältig ausgewählt. Wir legen großen Wert auf Zuverlässigkeit, Diskretion und professionelles Auftreten. Unsere Hausmeister verfügen über langjährige Erfahrung und werden regelmäßig geschult, damit Sie sich jederzeit sicher fühlen können.'
    }
  ];

  const images = [
    'https://public.readdy.ai/ai/img_res/47ee07b6547d1b0fcfd97192bf65ee18.jpg',
    'https://readdy.ai/api/search-image?query=facility%20maintenance%20worker%20performing%20repair%20work%20in%20apartment%20building%20hallway%2C%20professional%20tools%20and%20equipment%2C%20well-lit%20interior%2C%20clean%20modern%20setting&width=800&height=600&seq=hausmeister2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=professional%20gardener%20maintaining%20green%20areas%20around%20residential%20building%2C%20lawn%20mowing%20and%20hedge%20trimming%2C%20sunny%20day%2C%20neat%20landscaping&width=800&height=600&seq=hausmeister3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=winter%20service%20worker%20clearing%20snow%20from%20building%20entrance%20with%20professional%20snow%20removal%20equipment%2C%20early%20morning%2C%20safe%20walkways&width=800&height=600&seq=hausmeister4&orientation=landscape',
    'https://readdy.ai/api/search-image?query=facility%20manager%20checking%20building%20systems%20and%20utilities%20in%20technical%20room%2C%20professional%20inspection%2C%20modern%20equipment%2C%20organized%20workspace&width=800&height=600&seq=hausmeister5&orientation=landscape',
    'https://readdy.ai/api/search-image?query=professional%20cleaning%20and%20maintenance%20of%20apartment%20building%20common%20areas%2C%20stairwell%20and%20entrance%20hall%2C%20bright%20clean%20environment&width=800&height=600&seq=hausmeister6&orientation=landscape'
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${siteUrl}/hausmeister`,
    name: 'Hausmeisterservice Ratingen',
    description: 'Professioneller Hausmeisterservice in Ratingen: Wartung, Reparaturen, Grünpflege, Winterdienst und mehr. Zuverlässig und kompetent für Ihre Immobilie.',
    provider: {
      '@type': 'LocalBusiness',
      name: 'IGM Facility Service',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Industriestr. 37',
        addressLocality: 'Ratingen',
        postalCode: '40878',
        addressCountry: 'DE',
      },
      telephone: '+492102739807',
      email: 'info@igmfacilityservice.de',
    },
    areaServed: {
      '@type': 'City',
      name: 'Ratingen',
    },
    serviceType: 'Hausmeisterservice',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      priceSpecification: {
        '@type': 'PriceSpecification',
        priceCurrency: 'EUR',
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO
        title="Hausmeisterservice Ratingen | Wartung & Reparatur | IGM Facility Service"
        description="Professioneller Hausmeisterservice in Ratingen: Wartung, Reparaturen, Grünpflege, Winterdienst. Zuverlässig und kompetent. Jetzt Angebot anfragen bei IGM Facility Service!"
        keywords="Hausmeisterservice Ratingen, Hausmeister, Wartung, Reparatur, Grünpflege, Winterdienst, Immobilienpflege"
        canonical="/hausmeister"
        jsonLd={jsonLd}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 sm:pb-16 md:pb-20 bg-[#0D2B4E] overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)' }}></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-4 sm:mb-6">
              <i className="ri-home-smile-fill text-lg"></i>
              <span>Professioneller Hausmeisterservice</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">Ihr zuverlässiger Hausmeisterservice</h1>
            <p className="text-base sm:text-lg md:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed">Von Kleinreparaturen bis Winterdienst – wir kümmern uns um Ihre Immobilie</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="#quote-form" className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-[#0D2B4E] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-all shadow-xl cursor-pointer whitespace-nowrap">
                <span>Angebot anfragen</span>
                <i className="ri-arrow-right-line"></i>
              </a>
              <a href="tel:+492102739807" className="w-full sm:w-auto flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#0D2B4E] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill"></i>
                <span>02102 / 7398070</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">Was wir für Sie tun</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">Umfassender Service für Ihre Immobilie – alles aus einer Hand</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 hover:shadow-xl hover:border-[#0D2B4E]/20 transition-all group">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#0D2B4E] rounded-lg flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contract Options */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">Flexible Vertragsmodelle</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">Wählen Sie das Modell, das am besten zu Ihren Bedürfnissen passt</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {[
              { icon: 'ri-calendar-check-fill', iconBg: 'bg-gray-100', iconColor: 'text-gray-700', title: 'Einmalig', desc: 'Für einzelne Aufgaben und Projekte. Perfekt für gelegentliche Arbeiten oder spezielle Anforderungen.', btnClass: 'bg-gray-100 text-gray-900 hover:bg-gray-200', pkg: 'Einmalig', featured: false },
              { icon: 'ri-repeat-fill', iconBg: 'bg-[#0D2B4E]/10', iconColor: 'text-[#0D2B4E]', title: 'Regelmäßig', desc: 'Wiederkehrende Leistungen nach festem Plan. Ideal für kontinuierliche Betreuung Ihrer Immobilie.', btnClass: 'bg-[#0D2B4E] text-white hover:bg-[#0a2240]', pkg: 'Regelmäßig', featured: false },
              { icon: 'ri-vip-crown-fill', iconBg: 'bg-[#0D2B4E]', iconColor: 'text-white', title: 'Rundum-Sorglos', desc: 'Vollumfängliche Betreuung Ihrer Immobilie. Wir kümmern uns um alles – Sie haben keine Sorgen.', btnClass: 'bg-[#0D2B4E] text-white hover:bg-[#0a2240]', pkg: 'Rundum-Sorglos-Paket', featured: true },
            ].map((item, i) => (
              <div key={i} className={`bg-white rounded-2xl p-6 sm:p-8 transition-all relative ${item.featured ? 'border-2 border-[#0D2B4E] hover:shadow-xl' : 'border-2 border-gray-200 hover:border-[#0D2B4E]'}`}>
                {item.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0D2B4E] text-white px-4 py-1 rounded-full text-sm font-semibold whitespace-nowrap">Beliebt</div>
                )}
                <div className={`w-14 h-14 sm:w-16 sm:h-16 ${item.iconBg} rounded-full flex items-center justify-center mb-4 sm:mb-6`}>
                  <i className={`${item.icon} text-2xl sm:text-3xl ${item.iconColor}`}></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">{item.title}</h3>
                <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base leading-relaxed">{item.desc}</p>
                <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                  {['Keine Vertragsbindung', 'Transparente Abrechnung', 'Schnelle Verfügbarkeit'].map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#0D2B4E] text-lg mt-0.5"></i>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => { setFormData({...formData, package: item.pkg}); document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' }); }} className={`w-full py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${item.btnClass}`}>
                  Anfragen
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#0D2B4E]/5 rounded-full px-5 py-2 mb-5">
                <i className="ri-question-answer-line text-[#0D2B4E] text-lg"></i>
                <span className="text-sm font-semibold text-[#0D2B4E] tracking-wide">Häufige Fragen</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">Häufig gestellte Fragen</h2>
              <p className="text-base sm:text-lg text-gray-600">Antworten auf die wichtigsten Fragen zu unserem Hausmeisterservice in Ratingen</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={i}
                    className={`rounded-xl border transition-all duration-300 overflow-hidden ${
                      isOpen
                        ? 'border-[#0D2B4E]/30 shadow-md bg-[#0D2B4E]/[0.03]'
                        : 'border-gray-200 bg-white hover:border-[#0D2B4E]/20 hover:shadow-sm'
                    }`}
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-4 sm:py-5 text-left cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                        <a id={`faq-${i}`} className="scroll-mt-24">{faq.question}</a>
                      </h4>
                      <div
                        className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                          isOpen ? 'bg-[#0D2B4E] text-white rotate-180' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        <i className="ri-arrow-down-s-line text-lg"></i>
                      </div>
                    </button>

                    <div
                      className={`transition-all duration-300 ease-in-out ${
                        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      } overflow-hidden`}
                    >
                      <p className="px-5 sm:px-7 pb-5 sm:pb-6 text-sm sm:text-base text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-10 sm:mt-14">
              <p className="text-gray-500 text-sm mb-4">Ihre Frage ist nicht dabei?</p>
              <a
                href="#quote-form"
                className="inline-flex items-center gap-2 bg-[#0D2B4E] text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-[#0a2240] transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base shadow-md"
              >
                <i className="ri-mail-send-line text-lg"></i>
                <span>Jetzt anfragen</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">Unsere Arbeit in Bildern</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">Einblicke in unseren professionellen Hausmeisterservice</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {images.map((image, index) => (
              <div key={index} className="relative aspect-[4/3] rounded-xl overflow-hidden group cursor-pointer">
                <img src={image} alt={`Hausmeisterservice ${index + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">Angebot anfragen</h2>
              <p className="text-base sm:text-lg text-gray-600">Teilen Sie uns Ihre Anforderungen mit – wir erstellen Ihnen ein individuelles Angebot</p>
            </div>
            <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-200 shadow-lg" data-readdy-form>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name <span className="text-red-500">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">E-Mail <span className="text-red-500">*</span></label>
                  <input type="email" name="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm" required />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon <span className="text-red-500">*</span></label>
                  <input type="tel" name="phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse der Immobilie</label>
                  <input type="text" name="address" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm" />
                </div>
              </div>
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Gewünschtes Paket</label>
                <select name="package" value={formData.package} onChange={(e) => setFormData({...formData, package: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm">
                  <option value="">Bitte wählen...</option>
                  <option value="Einmalig">Einmalig</option>
                  <option value="Regelmäßig">Regelmäßig</option>
                  <option value="Rundum-Sorglos-Paket">Rundum-Sorglos-Paket</option>
                </select>
              </div>
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Ihre Nachricht</label>
                <textarea name="message" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} maxLength={500} rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm resize-none" placeholder="Beschreiben Sie Ihre Anforderungen..."></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">{formData.message.length}/500</div>
              </div>
              {submitStatus === 'success' && (
                <div className="mb-4 sm:mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                  <span className="text-green-800 text-sm font-medium">Vielen Dank! Wir erstellen Ihr individuelles Angebot und melden uns zeitnah bei Ihnen.</span>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mb-4 sm:mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <i className="ri-error-warning-fill text-2xl text-red-600"></i>
                  <span className="text-red-800 text-sm font-medium">Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.</span>
                </div>
              )}
              <button type="submit" disabled={isSubmitting} className="w-full bg-[#0D2B4E] text-white py-4 rounded-lg font-semibold hover:bg-[#0a2240] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                {isSubmitting ? <><i className="ri-loader-4-line animate-spin"></i><span>Wird gesendet...</span></> : <><span>Angebot anfragen</span><i className="ri-send-plane-fill"></i></>}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className={`fixed bottom-4 right-4 w-10 h-10 sm:w-12 sm:h-12 bg-[#0D2B4E] text-white rounded-full shadow-lg hover:bg-[#0a2240] transition-all flex items-center justify-center cursor-pointer z-40 ${showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16 pointer-events-none'}`}>
        <i className="ri-arrow-up-line text-lg sm:text-xl"></i>
      </button>
    </div>
  );
}